<template>
    <div :class="mainClass">
        <page-header v-if="includeHeader" :title="title" :items="breadcrumbs" />

        <tabs
            v-if="tabsElements.length"
            :items="tabsElements"
            :active-item="activeTab"
            :actions="tabActions"
            is-card
            class="mb-3"
            @action="onTabAction"
            @item-change="activeTab = $event"
        />

        <awesome-table
            :key="tableKey"
            :labels="tableLabels"
            :items="items"
            :pagination="pagination"
            :filters="filters"
            :options="options"
            :is-loading="isLoading"
            :get-row-class="getRowClass"
            :deleted-item-id="deletedItemId"
            @row-click="onRowClick"
            @pagination-change="onPaginationChange"
            @add-button-click="onAddButtonClick"
            @filter-change="onFilterChange"
        >
            <template #globalActionButtons>
                <button
                    v-if="importPath"
                    v-b-modal.import-csv
                    class="btn btn-success mr-1 mb-1"
                >
                    <i class="fas fa-upload" />
                    Import
                </button>

                <button
                    v-for="action in globalActions"
                    :key="action.modal"
                    :class="action.class"
                    @click.stop="action.method"
                >
                    <i :class="action.icon" />
                    {{ action.text }}
                </button>
            </template>

            <template #[`labels.actions`]="{ label }">
                <span style="color: gray">
                    {{ label }}
                </span>
            </template>
            <template #[`items.actions`]="{ item, index }">
                <button
                    v-for="(action, actionIndex) in tableActions"
                    v-show="isActionVisible(action, item)"
                    :key="`action_${actionIndex}`"
                    class="action-button"
                    @click.stop="callAction(action, item.id, index)"
                >
                    <i :class="action.icon" />
                </button>
            </template>
        </awesome-table>

        <import-csv-modal
            v-if="importPath"
            :path="importPath"
            @imported="fetchItems"
        />
        <confirmation-modal @agree="removeItem" @disagree="idToDelete = null" />

        <component
            :is="item.name"
            v-for="item of additionalComponents"
            :key="item.name"
            v-bind="item.props || {}"
            v-on="item.events || {}"
        />
    </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { StatusCodes as HTTP } from 'http-status-codes';
import Tabs from '@/components/elements/Tabs';
import ImportCsvModal from '@/components/elements/ImportCsvModal';
import ConfirmationModal from '@/components/elements/ConfirmationModal';

export default {
    components: {
        Tabs,
        ImportCsvModal,
        ConfirmationModal
    },

    data() {
        const {
            page: currentPage = 1,
            perPage = 10,
            sortBy = 'createdAt',
            order = 'DESC',
            q = null,
            filters = '{}'
        } = this.$route.query;

        return {
            mainClass: 'table-view',
            includeHeader: true,
            items: [],
            actions: [],
            includeDeleteAction: true,
            pagination: {
                currentPage: parseInt(currentPage),
                perPage: parseInt(perPage),
                total: 0,
                search: q || '',
                sortBy,
                descending: order.toLowerCase() === 'desc'
            },
            idToDelete: null,
            deletedItemId: null,
            filters: JSON.parse(filters),
            isLoading: false,
            activeTab: {},
            pageTitle: 'Items',
            importPath: null,
            tableKey: 0
        };
    },

    computed: {
        title() {
            return this.pageTitle;
        },

        breadcrumbs() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: this.pageTitle,
                    active: true
                }
            ];
        },
        globalActions() {
            return [];
        },

        labels() {
            return [];
        },

        options() {
            return {};
        },

        tabsElements() {
            return [];
        },

        tabActions() {
            return [];
        },

        tableLabels() {
            const labels = [...this.labels];

            if (this.tableActions.length) {
                labels.push({
                    value: 'actions',
                    sortable: false,
                    width: '100px',
                    filterable: false
                });
            }

            return labels;
        },

        tableActions() {
            const actions = [...this.actions];

            if (this.includeDeleteAction) {
                actions.push({
                    icon: 'far fa-trash-alt',
                    method: 'showDeleteModal'
                });
            }

            return actions;
        },

        additionalFetchData() {
            return {};
        },

        additionalComponents() {
            return [];
        }
    },

    created() {
        this.fetchItems();
    },

    methods: {
        getItems() {
            return {
                rows: [],
                count: 0
            };
        },
        deleteItem() {},
        onRowClick() {},
        onAddButtonClick() {},
        onTabAction() {},

        async fetchItems() {
            this.isLoading = true;

            try {
                const data = {
                    ...this.pagination,
                    filters: this.filters,
                    ...this.additionalFetchData
                };

                const { count, rows } = await this.getItems(data);

                this.items = rows;
                this.pagination.total = count;
            } catch (err) {
                console.error(err);

                this.$toasterError();
            }

            this.isLoading = false;
        },

        onPaginationChange(pagination) {
            this.pagination = {
                ...pagination
            };

            this.fetchItems();
        },

        onFilterChange(filters) {
            this.filters = { ...filters };

            this.fetchItems();
        },

        showDeleteModal(id) {
            this.idToDelete = id;
            this.$bvModal.show('confirm-modal');
        },

        callAction(action, id, index) {
            this[action.method](id, index);
        },

        async removeItem() {
            try {
                await this.deleteItem(this.idToDelete);

                this.items = this.items.filter(
                    item => item.id !== this.idToDelete
                );
                this.deletedItemId = this.idToDelete;
                this.idToDelete = null;
                this.pagination.total--;

                this.$toaster('Item has been deleted.');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        isActionVisible(action, item) {
            const { where } = action;

            if (!where) {
                return true;
            }

            for (const key in where) {
                const { value: condition, operator = 'equals' } = where[key];

                let value = cloneDeep(item);
                const properties = key.split('.');

                for (let property of properties) {
                    const isTableElement = property.includes('[');

                    if (isTableElement) {
                        let elements = property.split('[');

                        if (elements.length < 2) {
                            return false;
                        }

                        value = value[elements[0]];
                        elements = elements[1].split(']');

                        try {
                            property = parseInt(elements[0]);
                        } catch (error) {
                            return false;
                        }
                    }

                    if (value[property] === undefined) {
                        return false;
                    }

                    value = value[property];
                }

                if (operator === 'equals' && condition !== value) {
                    return false;
                }

                if (operator === 'includes' && !value.includes(condition)) {
                    return false;
                }

                if (operator === 'not' && condition === value) {
                    return false;
                }

                if (operator === 'in' && !condition.includes(value)) {
                    return false;
                }
            }

            return true;
        },

        getRowClass() {
            return '';
        }
    }
};
</script>
